import React, { useState } from 'react';
import { motion } from 'framer-motion';

import { PageMeta } from '../../components';

const c = {
  teal: '#1565D8',
  tealDark: '#0F4EAB',
  tealLight: 'rgba(21,101,216,0.08)',
  tealBorder: 'rgba(21,101,216,0.20)',
  navy: '#0F1C33',
  navyMid: '#1B2B3A',
  text: '#1B2B3A',
  textMid: '#4A5E6E',
  textLight: '#7A8C99',
  bg: '#F5F8FF',
  bgAlt: '#EEF4FF',
  white: '#FFFFFF',
  border: '#DDE7EE',
  gold: '#F4A800',
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = { visible: { transition: { staggerChildren: 0.09 } } };

function AnimatedSection({ children, style }) {
  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-80px' }}
      variants={stagger}
      style={style}
    >
      {children}
    </motion.div>
  );
}

function Pill({ children, teal }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: teal ? 'rgba(26,158,133,0.12)' : 'rgba(15,32,51,0.07)',
        color: teal ? c.teal : c.navy,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        padding: '6px 14px',
        borderRadius: 40,
        marginBottom: 16,
      }}
    >
      {teal && (
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: c.teal,
            display: 'inline-block',
          }}
        />
      )}
      {children}
    </span>
  );
}

function Stat({ value, label, sub, icon }) {
  return (
    <motion.div
      variants={fadeUp}
      style={{
        textAlign: 'center',
        padding: '28px 20px',
        background: c.white,
        border: `1px solid ${c.border}`,
        borderRadius: 20,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle teal top accent bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: '20%',
          right: '20%',
          height: 3,
          background: `linear-gradient(90deg, transparent, ${c.teal}, transparent)`,
          borderRadius: '0 0 4px 4px',
        }}
      />
      {icon && <div style={{ fontSize: 22, marginBottom: 8 }}>{icon}</div>}
      <div
        style={{
          fontSize: 'clamp(30px,3.5vw,48px)',
          fontWeight: 800,
          color: c.teal,
          fontFamily: 'DM Sans, sans-serif',
          lineHeight: 1,
          letterSpacing: '-1px',
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 14, fontWeight: 600, color: c.navy, marginTop: 10, lineHeight: 1.4 }}>
        {label}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: c.textLight, marginTop: 5, lineHeight: 1.4 }}>{sub}</div>
      )}
    </motion.div>
  );
}

const TIERS = [
  {
    name: 'Dealer Starter',
    price: '$0',
    period: '/mo',
    earlyBird: true,
    desc: 'Early bird: first 100 dealers pay nothing. Locks in at $999.99/mo after launch.',
    listings: 'Up to 25 vehicles',
    highlight: false,
    features: [
      'Dealer storefront page',
      'Up to 25 active listings',
      'AI listing descriptions',
      'Standard lead inbox',
      'Verified dealer badge',
      'Basic analytics dashboard',
      'Email support',
    ],
    cta: 'Get Started',
  },
  {
    name: 'Dealer Pro',
    price: '$999.99',
    period: '/mo',
    desc: 'For established dealers who need lead volume and buyer matching.',
    listings: 'Up to 100 vehicles',
    highlight: true,
    features: [
      'Everything in Starter',
      'Up to 100 active listings',
      'Priority search placement',
      'Pre-qualified buyer matching',
      'Lead routing & CRM export',
      'Financing partner integration',
      'Advanced analytics + ROI reports',
      'Dedicated account manager',
      'Phone & priority email support',
    ],
    cta: 'Start Free Trial',
  },
  {
    name: 'Enterprise Fleet',
    price: 'Custom',
    period: '',
    desc: 'For dealer groups, auction houses, and fleet operators at scale.',
    listings: 'Unlimited inventory',
    highlight: false,
    features: [
      'Everything in Pro',
      'Unlimited active listings',
      'Bulk CSV / API inventory upload',
      'White-label storefront option',
      'Multi-location management',
      'Real-time inventory sync',
      'Custom financing programs',
      'Fleet & commercial vehicle tools',
      'SLA-backed uptime guarantee',
      'Dedicated integration team',
    ],
    cta: 'Contact Sales',
  },
];

const FEATURES = [
  {
    icon: '📦',
    title: 'Bulk inventory upload',
    body:
      'Push hundreds of vehicles via CSV or REST API. Listings go live with AI-generated descriptions, market-value pricing, and photos — automatically.',
  },
  {
    icon: '📊',
    title: 'Real-time analytics dashboard',
    body:
      'Track views, leads, conversion rate, and average days-to-sell per vehicle. Know which listings perform and why.',
  },
  {
    icon: '🎯',
    title: 'Pre-qualified buyer matching',
    body:
      'AI matches your inventory to buyers who are financing-approved and actively searching. No cold leads.',
  },
  {
    icon: '🔌',
    title: 'API & CRM integration',
    body:
      'Connect your existing DMS or CRM via REST API. Two-way sync keeps your systems and your SkipTheDealerships storefront in lockstep.',
  },
  {
    icon: '🏅',
    title: 'Verified dealer badge',
    body:
      'Your storefront carries a verified badge — showing buyers your dealership is KYC-confirmed, licensed, and trustworthy.',
  },
  {
    icon: '💳',
    title: 'Financing partner network',
    body:
      'Tap into our network of Canadian lenders. Pre-qualify buyers inline. Send deal packages directly from your dashboard.',
  },
  {
    icon: '🗺️',
    title: 'Multi-location management',
    body:
      'Run one account across multiple lots. Assign inventory to locations, manage staff logins, and see consolidated reporting.',
  },
  {
    icon: '⚡',
    title: '60-second buyer alerts',
    body:
      'The moment a listing matches a saved buyer search, they get an instant notification — driving urgency and faster turnover.',
  },
];

const HOW_IT_WORKS = [
  {
    n: '01',
    title: 'Apply & get verified',
    body:
      'Submit your dealer licence. Our team verifies your business within 1 business day and activates your storefront.',
  },
  {
    n: '02',
    title: 'Upload your inventory',
    body:
      'Use the dashboard, CSV bulk upload, or our REST API. AI writes your descriptions and pulls market values automatically.',
  },
  {
    n: '03',
    title: 'Receive qualified leads',
    body:
      'Buyers are matched to your listings based on their pre-qualification profile. Leads arrive in your inbox or CRM.',
  },
  {
    n: '04',
    title: 'Close & measure',
    body:
      'Track every deal from first view to close. See your ROI per vehicle and optimize your inventory mix with live data.',
  },
];

export default function DealerPage() {
  const [formSent, setFormSent] = useState(false);
  const [form, setForm] = useState({
    name: '',
    dealership: '',
    email: '',
    phone: '',
    inventory: '',
    tier: 'Dealer Pro',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Login card state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    if (!loginEmail || !loginPassword) {
      setLoginError('Please enter your email and password.');
      return;
    }
    setLoginLoading(true);
    setLoginError('');
    try {
      const resp = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
        credentials: 'include',
      });
      if (resp.ok || resp.redirected) {
        window.location.href = '/dealer/dashboard';
      } else {
        // Fall back to Sharetribe login page with email pre-filled
        window.location.href = `/login?email=${encodeURIComponent(
          loginEmail
        )}&next=/dealer/dashboard`;
      }
    } catch {
      // Fall back to Sharetribe login page
      window.location.href = `/login?email=${encodeURIComponent(
        loginEmail
      )}&next=/dealer/dashboard`;
    } finally {
      setLoginLoading(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    try {
      const resp = await fetch('/api/dealer-apply', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await resp.json();
      if (data.success) {
        setFormSent(true);
      } else {
        setSubmitError(
          data.error || 'Something went wrong. Please email sales@skipthedealerships.ca directly.'
        );
      }
    } catch {
      setSubmitError('Network error. Please email sales@skipthedealerships.ca directly.');
    } finally {
      setSubmitting(false);
    }
  }

  const HERO_ICONS = [
    {
      icon: (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
      label: 'Verified Buyers',
    },
    {
      icon: (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="2" y="3" width="20" height="14" rx="2" />
          <line x1="8" y1="21" x2="16" y2="21" />
          <line x1="12" y1="17" x2="12" y2="21" />
        </svg>
      ),
      label: 'Bulk Inventory Management',
    },
    {
      icon: (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="3" />
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="2" x2="12" y2="5" />
          <line x1="12" y1="19" x2="12" y2="22" />
          <line x1="2" y1="12" x2="5" y2="12" />
          <line x1="19" y1="12" x2="22" y2="12" />
        </svg>
      ),
      label: 'Smart Buyer Matching',
    },
    {
      icon: (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="20" x2="18" y2="10" />
          <line x1="12" y1="20" x2="12" y2="4" />
          <line x1="6" y1="20" x2="6" y2="14" />
        </svg>
      ),
      label: 'Real-Time ROI Analytics',
    },
    {
      icon: (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      ),
      label: 'Secure & Trusted',
    },
  ];

  return (
    <>
      <PageMeta
        path="/dealers"
        title="Dealer Partner Portal"
        description="The platform built to move your inventory — fast, at scale, measurably. Join dealers using SkipTheDealerships.ai."
      />
      <div
        style={{ fontFamily: 'DM Sans, system-ui, sans-serif', color: c.text, background: c.white }}
      >
        {/* ── HERO — split layout ── */}
        <section
          style={{
            background: c.white,
            borderBottom: `1px solid ${c.border}`,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Subtle top photo strip */}
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: 0,
              bottom: 0,
              width: '42%',
              zIndex: 0,
              overflow: 'hidden',
              display: 'none',
            }}
            className="heroBg"
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(to right, #fff 0%, transparent 30%)',
                zIndex: 1,
              }}
            />
          </div>

          <div
            className="dealerHeroGrid"
            style={{
              maxWidth: 1200,
              margin: '0 auto',
              padding: 'clamp(60px,8vw,100px) 32px',
              display: 'grid',
              gridTemplateColumns: '1fr 440px',
              gap: 60,
              alignItems: 'center',
              position: 'relative',
              zIndex: 1,
            }}
          >
            {/* ── LEFT: marketing ── */}
            <AnimatedSection>
              <motion.div variants={fadeUp}>
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: c.teal,
                    display: 'block',
                    marginBottom: 18,
                  }}
                >
                  Dealer Partner Portal
                </span>
              </motion.div>
              <motion.h1
                variants={fadeUp}
                style={{
                  fontSize: 'clamp(32px,4.5vw,60px)',
                  fontWeight: 800,
                  color: c.navy,
                  lineHeight: 1.1,
                  letterSpacing: '-1.5px',
                  margin: '0 0 24px',
                }}
              >
                The platform built to move your inventory —{' '}
                <span style={{ color: c.teal }}>fast, at scale, measurably.</span>
              </motion.h1>
              <motion.p
                variants={fadeUp}
                style={{
                  fontSize: 17,
                  color: c.textMid,
                  lineHeight: 1.8,
                  marginBottom: 36,
                  maxWidth: 520,
                }}
              >
                SkipTheDealerships.ai gives Canadian dealers a verified storefront, bulk inventory
                tools, pre-qualified buyer matching, and real-time ROI analytics — all in one
                platform.
              </motion.p>
              <motion.div
                variants={fadeUp}
                style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 40 }}
              >
                <a
                  href="/dealer/register"
                  style={{
                    background: c.teal,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 16,
                    padding: '15px 30px',
                    borderRadius: 10,
                    textDecoration: 'none',
                    boxShadow: `0 4px 18px rgba(21,101,216,0.3)`,
                  }}
                >
                  Apply Now — It's Free →
                </a>
                <a
                  href="#how-it-works"
                  style={{
                    background: 'transparent',
                    color: c.navy,
                    fontWeight: 600,
                    fontSize: 15,
                    padding: '15px 28px',
                    borderRadius: 10,
                    textDecoration: 'none',
                    border: `1.5px solid ${c.border}`,
                  }}
                >
                  See How It Works
                </a>
              </motion.div>

              {/* Feature icons row */}
              <motion.div variants={fadeUp} style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
                {HERO_ICONS.map((item, i) => (
                  <div
                    key={i}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 8,
                      minWidth: 80,
                    }}
                  >
                    <div
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: 14,
                        border: `1.5px solid ${c.border}`,
                        background: c.bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: c.teal,
                      }}
                    >
                      {item.icon}
                    </div>
                    <span
                      style={{
                        fontSize: 11.5,
                        fontWeight: 600,
                        color: c.textMid,
                        textAlign: 'center',
                        lineHeight: 1.3,
                      }}
                    >
                      {item.label}
                    </span>
                  </div>
                ))}
              </motion.div>

              {/* Social proof bar */}
              <motion.div
                variants={fadeUp}
                style={{
                  marginTop: 36,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 14,
                  background: c.bg,
                  border: `1px solid ${c.border}`,
                  borderRadius: 12,
                  padding: '14px 20px',
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: c.tealLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: c.teal,
                    fontSize: 18,
                    flexShrink: 0,
                  }}
                >
                  👥
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: c.navy }}>
                    Join 100+ Canadian dealers already growing with Skip
                  </div>
                  <div style={{ fontSize: 13, color: c.textLight }}>
                    More exposure. Better buyers. Higher conversions.
                  </div>
                </div>
              </motion.div>

              {/* Trust tags */}
              <motion.div
                variants={fadeUp}
                style={{ marginTop: 24, display: 'flex', gap: 20, flexWrap: 'wrap' }}
              >
                {[
                  { icon: '✓', label: 'No setup fees' },
                  { icon: '⏱', label: 'Live within 24 hrs' },
                  { icon: '🍁', label: 'Canadian dealers only' },
                ].map((t, i) => (
                  <span
                    key={i}
                    style={{
                      fontSize: 13,
                      color: c.textLight,
                      fontWeight: 500,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 5,
                    }}
                  >
                    <span style={{ color: c.teal }}>{t.icon}</span> {t.label}
                  </span>
                ))}
              </motion.div>
            </AnimatedSection>

            {/* ── RIGHT: login card ── */}
            <motion.div
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{
                background: c.white,
                border: `1.5px solid ${c.border}`,
                borderRadius: 20,
                boxShadow: '0 8px 48px rgba(15,28,51,0.10)',
                padding: '40px 36px',
              }}
            >
              {/* Card header */}
              <div style={{ textAlign: 'center', marginBottom: 28 }}>
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: '50%',
                    border: `2px solid ${c.tealBorder}`,
                    background: c.tealLight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 16px',
                    color: c.teal,
                  }}
                >
                  <svg
                    width="26"
                    height="26"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <h2 style={{ fontSize: 22, fontWeight: 800, color: c.navy, margin: '0 0 6px' }}>
                  Dealer Partner Login
                </h2>
                <p style={{ fontSize: 14, color: c.textLight, lineHeight: 1.6, margin: 0 }}>
                  Access your dashboard, manage inventory,
                  <br />
                  and connect with more buyers.
                </p>
              </div>

              {/* Login form */}
              <form onSubmit={handleLogin}>
                {loginError && (
                  <div
                    style={{
                      background: '#FEF2F2',
                      border: '1px solid #FECACA',
                      borderRadius: 8,
                      padding: '10px 14px',
                      fontSize: 13,
                      color: '#B91C1C',
                      marginBottom: 16,
                    }}
                  >
                    {loginError}
                  </div>
                )}

                <label
                  style={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 600,
                    color: c.navy,
                    marginBottom: 6,
                  }}
                >
                  Email
                </label>
                <div style={{ position: 'relative', marginBottom: 18 }}>
                  <span
                    style={{
                      position: 'absolute',
                      left: 13,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: c.textLight,
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="2" y="4" width="20" height="16" rx="2" />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                  </span>
                  <input
                    type="email"
                    value={loginEmail}
                    onChange={e => setLoginEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    style={{
                      width: '100%',
                      padding: '12px 14px 12px 40px',
                      borderRadius: 9,
                      border: `1.5px solid ${c.border}`,
                      fontSize: 15,
                      fontFamily: 'DM Sans, sans-serif',
                      outline: 'none',
                      color: c.navy,
                      background: c.bg,
                      boxSizing: 'border-box',
                    }}
                    onFocus={e => {
                      e.target.style.borderColor = c.teal;
                    }}
                    onBlur={e => {
                      e.target.style.borderColor = c.border;
                    }}
                  />
                </div>

                <label
                  style={{
                    display: 'block',
                    fontSize: 13,
                    fontWeight: 600,
                    color: c.navy,
                    marginBottom: 6,
                  }}
                >
                  Password
                </label>
                <div style={{ position: 'relative', marginBottom: 6 }}>
                  <span
                    style={{
                      position: 'absolute',
                      left: 13,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: c.textLight,
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  </span>
                  <input
                    type={showPass ? 'text' : 'password'}
                    value={loginPassword}
                    onChange={e => setLoginPassword(e.target.value)}
                    placeholder="Enter your password"
                    required
                    style={{
                      width: '100%',
                      padding: '12px 44px 12px 40px',
                      borderRadius: 9,
                      border: `1.5px solid ${c.border}`,
                      fontSize: 15,
                      fontFamily: 'DM Sans, sans-serif',
                      outline: 'none',
                      color: c.navy,
                      background: c.bg,
                      boxSizing: 'border-box',
                    }}
                    onFocus={e => {
                      e.target.style.borderColor = c.teal;
                    }}
                    onBlur={e => {
                      e.target.style.borderColor = c.border;
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(p => !p)}
                    style={{
                      position: 'absolute',
                      right: 13,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: c.textLight,
                      padding: 0,
                    }}
                  >
                    {showPass ? (
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg
                        width="17"
                        height="17"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 22,
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 7,
                      fontSize: 13,
                      color: c.textMid,
                      cursor: 'pointer',
                    }}
                  >
                    <input type="checkbox" style={{ accentColor: c.teal, width: 15, height: 15 }} />{' '}
                    Remember me
                  </label>
                  <a
                    href="/recover-password"
                    style={{ fontSize: 13, color: c.teal, textDecoration: 'none', fontWeight: 600 }}
                  >
                    Forgot password?
                  </a>
                </div>

                <button
                  type="submit"
                  disabled={loginLoading}
                  style={{
                    width: '100%',
                    background: c.teal,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 16,
                    padding: '14px',
                    borderRadius: 10,
                    border: 'none',
                    cursor: loginLoading ? 'not-allowed' : 'pointer',
                    fontFamily: 'DM Sans, sans-serif',
                    opacity: loginLoading ? 0.7 : 1,
                    boxShadow: `0 4px 16px rgba(21,101,216,0.25)`,
                  }}
                >
                  {loginLoading ? 'Signing in…' : 'Log In'}
                </button>
              </form>

              <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '20px 0' }}>
                <div style={{ flex: 1, height: 1, background: c.border }} />
                <span style={{ fontSize: 12, color: c.textLight, fontWeight: 500 }}>OR</span>
                <div style={{ flex: 1, height: 1, background: c.border }} />
              </div>

              <a
                href="/api/auth/google"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  width: '100%',
                  padding: '13px',
                  borderRadius: 10,
                  border: `1.5px solid ${c.border}`,
                  background: c.white,
                  textDecoration: 'none',
                  color: c.navy,
                  fontWeight: 600,
                  fontSize: 15,
                  fontFamily: 'DM Sans, sans-serif',
                  boxSizing: 'border-box',
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Continue with Google
              </a>

              <p
                style={{
                  textAlign: 'center',
                  fontSize: 13,
                  color: c.textLight,
                  marginTop: 20,
                  marginBottom: 0,
                }}
              >
                Not a dealer partner?{' '}
                <a
                  href="/dealer/register"
                  style={{ color: c.teal, fontWeight: 700, textDecoration: 'none' }}
                >
                  Apply Now
                </a>
              </p>
            </motion.div>
          </div>

          {/* Responsive: stack on mobile */}
          <style>{`
          @media(max-width:900px){
            .dealerHeroGrid { grid-template-columns: 1fr !important; }
          }
        `}</style>
        </section>

        {/* ── PORTAL PREVIEW ── */}
        <section
          style={{
            background: c.bgAlt,
            padding: '72px 32px 0',
            textAlign: 'center',
            borderTop: `1px solid ${c.border}`,
          }}
        >
          <div style={{ maxWidth: 1160, margin: '0 auto' }}>
            <p
              style={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: c.teal,
                marginBottom: 12,
              }}
            >
              Dealer Partner Portal
            </p>
            <h2
              style={{
                fontSize: 'clamp(22px,3vw,36px)',
                fontWeight: 800,
                color: c.navy,
                margin: '0 0 12px',
                letterSpacing: '-0.5px',
              }}
            >
              See what dealers see.
            </h2>
            <p
              style={{
                color: c.textMid,
                fontSize: 15.5,
                maxWidth: 520,
                margin: '0 auto 44px',
                lineHeight: 1.75,
              }}
            >
              One dashboard for your entire inventory pipeline — from VIN verification to buyer
              match to closed deal.
            </p>
            <div
              style={{
                borderRadius: '16px 16px 0 0',
                overflow: 'hidden',
                boxShadow: '0 -4px 48px rgba(21,101,216,0.12), 0 0 0 1px rgba(21,101,216,0.08)',
                lineHeight: 0,
              }}
            >
              <img
                src="/static/images/dealer-portal-preview.png"
                alt="SkipTheDealerships.ai Dealer Partner Portal"
                style={{ width: '100%', maxWidth: '100%', display: 'block' }}
              />
            </div>
          </div>
        </section>

        {/* ── STATS — measurable for today ── */}
        <section
          style={{ padding: '70px 32px', background: c.bg, borderBottom: `1px solid ${c.border}` }}
        >
          <div style={{ maxWidth: 1160, margin: '0 auto' }}>
            <AnimatedSection>
              <motion.p
                variants={fadeUp}
                style={{
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 700,
                  color: c.textLight,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  marginBottom: 36,
                }}
              >
                Platform performance — measured in real results
              </motion.p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: 16,
                }}
              >
                <Stat
                  icon="🚀"
                  value="14 days"
                  label="Avg. days to first qualified lead"
                  sub="Per new dealer storefront"
                />
                <Stat
                  icon="📈"
                  value="3.8×"
                  label="More leads vs. Kijiji listing alone"
                  sub="Dealer accounts active 90+ days"
                />
                <Stat
                  icon="🤖"
                  value="100%"
                  label="Listings AI-described & priced"
                  sub="No copywriting needed"
                />
                <Stat
                  icon="⚡"
                  value="60 sec"
                  label="Buyer alert after listing goes live"
                  sub="Instant match notification"
                />
                <Stat icon="🎯" value="$0" label="Setup cost" sub="No onboarding or hidden fees" />
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* ── FEATURES — scalable for future ── */}
        <section style={{ padding: '100px 32px', background: c.white }}>
          <div style={{ maxWidth: 1160, margin: '0 auto' }}>
            <AnimatedSection>
              <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 60 }}>
                <Pill>Platform capabilities</Pill>
                <h2
                  style={{
                    fontSize: 'clamp(26px,3.5vw,44px)',
                    fontWeight: 700,
                    color: c.navy,
                    margin: '0 0 16px',
                    letterSpacing: '-0.5px',
                  }}
                >
                  Built to scale with your business.
                </h2>
                <p
                  style={{
                    color: c.textMid,
                    fontSize: 17,
                    maxWidth: 560,
                    margin: '0 auto',
                    lineHeight: 1.75,
                  }}
                >
                  Whether you have 10 vehicles or 10,000 — the infrastructure stays the same. You
                  grow, we scale.
                </p>
              </motion.div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                  gap: 20,
                }}
              >
                {FEATURES.map((f, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    style={{
                      background: c.bg,
                      border: `1px solid ${c.border}`,
                      borderRadius: 16,
                      padding: '28px 26px',
                    }}
                  >
                    <div style={{ fontSize: 28, marginBottom: 14 }}>{f.icon}</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: c.navy, marginBottom: 8 }}>
                      {f.title}
                    </div>
                    <div style={{ fontSize: 14, color: c.textMid, lineHeight: 1.7 }}>{f.body}</div>
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section
          id="how-it-works"
          style={{ padding: '100px 32px', background: c.bgAlt, borderTop: `1px solid ${c.border}` }}
        >
          <div style={{ maxWidth: 1160, margin: '0 auto' }}>
            <AnimatedSection>
              <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 60 }}>
                <Pill teal>How it works</Pill>
                <h2
                  style={{
                    fontSize: 'clamp(26px,3.5vw,44px)',
                    fontWeight: 700,
                    color: c.navy,
                    margin: '0 0 16px',
                    letterSpacing: '-0.5px',
                  }}
                >
                  Live in 4 steps.
                </h2>
                <p
                  style={{
                    color: c.textMid,
                    fontSize: 17,
                    maxWidth: 500,
                    margin: '0 auto',
                    lineHeight: 1.75,
                  }}
                >
                  From application to your first qualified lead — most dealers are live within 24
                  hours.
                </p>
              </motion.div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                  gap: 24,
                }}
              >
                {HOW_IT_WORKS.map((step, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    style={{
                      background: c.white,
                      border: `1px solid ${c.border}`,
                      borderRadius: 16,
                      padding: '32px 28px',
                      position: 'relative',
                    }}
                  >
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: '0.12em',
                        color: c.teal,
                        marginBottom: 16,
                        fontFamily: 'monospace',
                      }}
                    >
                      {step.n}
                    </div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: c.navy, marginBottom: 10 }}>
                      {step.title}
                    </div>
                    <div style={{ fontSize: 14, color: c.textMid, lineHeight: 1.7 }}>
                      {step.body}
                    </div>
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* ── PRICING ── */}
        <section style={{ padding: '100px 32px', background: c.white }}>
          <div style={{ maxWidth: 1160, margin: '0 auto' }}>
            <AnimatedSection>
              <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 60 }}>
                <Pill>Pricing</Pill>
                <h2
                  style={{
                    fontSize: 'clamp(26px,3.5vw,44px)',
                    fontWeight: 700,
                    color: c.navy,
                    margin: '0 0 16px',
                    letterSpacing: '-0.5px',
                  }}
                >
                  Transparent pricing. No hidden fees.
                </h2>
                <p
                  style={{
                    color: c.textMid,
                    fontSize: 17,
                    maxWidth: 480,
                    margin: '0 auto',
                    lineHeight: 1.75,
                  }}
                >
                  Start with what you need today. Upgrade as your inventory grows.
                </p>
              </motion.div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: 24,
                  alignItems: 'start',
                }}
              >
                {TIERS.map((tier, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    style={{
                      background: tier.highlight ? c.navy : c.white,
                      border: tier.highlight ? `2px solid ${c.teal}` : `1px solid ${c.border}`,
                      borderRadius: 20,
                      padding: '36px 32px',
                      position: 'relative',
                    }}
                  >
                    {tier.highlight && (
                      <div
                        style={{
                          position: 'absolute',
                          top: -14,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: c.teal,
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '5px 16px',
                          borderRadius: 40,
                          letterSpacing: '0.06em',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        MOST POPULAR
                      </div>
                    )}
                    {tier.earlyBird && (
                      <div
                        style={{
                          position: 'absolute',
                          top: -14,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          background: '#F59E0B',
                          color: '#fff',
                          fontSize: 11,
                          fontWeight: 700,
                          padding: '5px 16px',
                          borderRadius: 40,
                          letterSpacing: '0.06em',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        🐦 EARLY BIRD — FIRST 100 DEALERS FREE
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: '0.1em',
                        textTransform: 'uppercase',
                        color: tier.highlight ? 'rgba(255,255,255,0.5)' : c.textLight,
                        marginBottom: 8,
                      }}
                    >
                      {tier.name}
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        gap: 4,
                        marginBottom: tier.earlyBird ? 4 : 8,
                      }}
                    >
                      <span
                        style={{
                          fontSize: 'clamp(32px,4vw,48px)',
                          fontWeight: 800,
                          color: tier.highlight ? '#fff' : c.navy,
                          lineHeight: 1,
                        }}
                      >
                        {tier.price}
                      </span>
                      {tier.period && (
                        <span
                          style={{
                            fontSize: 15,
                            color: tier.highlight ? 'rgba(255,255,255,0.5)' : c.textLight,
                          }}
                        >
                          {tier.period}
                        </span>
                      )}
                    </div>
                    {tier.earlyBird && (
                      <div style={{ fontSize: 12, color: c.textLight, marginBottom: 8 }}>
                        then $999.99/mo after first 100 dealers
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: c.teal, fontWeight: 600, marginBottom: 14 }}>
                      {tier.listings}
                    </div>
                    <p
                      style={{
                        fontSize: 14,
                        color: tier.highlight ? 'rgba(255,255,255,0.65)' : c.textMid,
                        lineHeight: 1.6,
                        marginBottom: 24,
                      }}
                    >
                      {tier.desc}
                    </p>
                    <a
                      href="/dealer/register"
                      style={{
                        display: 'block',
                        textAlign: 'center',
                        fontWeight: 700,
                        fontSize: 14,
                        padding: '13px 0',
                        borderRadius: 10,
                        textDecoration: 'none',
                        marginBottom: 28,
                        background: tier.highlight ? c.teal : 'transparent',
                        color: tier.highlight ? '#fff' : c.teal,
                        border: tier.highlight ? 'none' : `1.5px solid ${c.teal}`,
                      }}
                    >
                      {tier.cta}
                    </a>
                    <div
                      style={{
                        borderTop: `1px solid ${
                          tier.highlight ? 'rgba(255,255,255,0.1)' : c.border
                        }`,
                        paddingTop: 20,
                      }}
                    >
                      {tier.features.map((f, j) => (
                        <div
                          key={j}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 10,
                            marginBottom: 10,
                          }}
                        >
                          <span
                            style={{ color: c.teal, fontSize: 15, marginTop: 1, flexShrink: 0 }}
                          >
                            ✓
                          </span>
                          <span
                            style={{
                              fontSize: 13,
                              color: tier.highlight ? 'rgba(255,255,255,0.75)' : c.textMid,
                              lineHeight: 1.5,
                            }}
                          >
                            {f}
                          </span>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </section>

        {/* ── API / SCALABILITY CALLOUT ── */}
        <section
          style={{ padding: '80px 32px', background: c.bg, borderTop: `1px solid ${c.border}` }}
        >
          <div style={{ maxWidth: 1160, margin: '0 auto' }}>
            <AnimatedSection>
              <motion.div
                variants={fadeUp}
                style={{
                  background: c.navy,
                  borderRadius: 24,
                  padding: 'clamp(40px,6vw,64px)',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 40,
                  alignItems: 'center',
                }}
              >
                <div>
                  <Pill teal>For enterprise & dealer groups</Pill>
                  <h3
                    style={{
                      fontSize: 'clamp(22px,3vw,36px)',
                      fontWeight: 700,
                      color: '#fff',
                      margin: '12px 0 16px',
                      letterSpacing: '-0.5px',
                    }}
                  >
                    Massive inventory? We built the pipes for it.
                  </h3>
                  <p
                    style={{
                      color: 'rgba(255,255,255,0.65)',
                      fontSize: 16,
                      lineHeight: 1.75,
                      maxWidth: 560,
                    }}
                  >
                    Our REST API supports bulk inventory sync from any DMS — Dealer Socket, PBS,
                    CDK, or custom. Push thousands of vehicles in minutes. Auto-pricing,
                    auto-descriptions, auto-matching. Real-time webhook callbacks on every lead.
                  </p>
                  <div style={{ display: 'flex', gap: 32, marginTop: 28, flexWrap: 'wrap' }}>
                    {[
                      { label: 'REST API', val: 'Full CRUD' },
                      { label: 'Bulk upload', val: 'CSV + JSON' },
                      { label: 'Webhooks', val: 'Real-time' },
                      { label: 'DMS support', val: 'Major providers' },
                    ].map((item, i) => (
                      <div key={i}>
                        <div
                          style={{
                            fontSize: 11,
                            color: 'rgba(255,255,255,0.4)',
                            fontWeight: 600,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            marginBottom: 4,
                          }}
                        >
                          {item.label}
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: c.teal }}>
                          {item.val}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ textAlign: 'center', minWidth: 160 }}>
                  <a
                    href="mailto:sales@skipthedealerships.ca?subject=Enterprise API Inquiry"
                    style={{
                      display: 'inline-block',
                      background: c.teal,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 15,
                      padding: '16px 28px',
                      borderRadius: 12,
                      textDecoration: 'none',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Talk to Sales
                  </a>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 10 }}>
                    No commitment required
                  </div>
                </div>
              </motion.div>
            </AnimatedSection>
          </div>
        </section>

        {/* ── APPLY FORM ── */}
        <section id="apply" style={{ padding: '100px 32px', background: c.white }}>
          <div style={{ maxWidth: 680, margin: '0 auto' }}>
            <AnimatedSection>
              <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 48 }}>
                <Pill teal>Apply now</Pill>
                <h2
                  style={{
                    fontSize: 'clamp(26px,3.5vw,40px)',
                    fontWeight: 700,
                    color: c.navy,
                    margin: '0 0 16px',
                    letterSpacing: '-0.5px',
                  }}
                >
                  Get your storefront live today.
                </h2>
                <p style={{ color: c.textMid, fontSize: 16, lineHeight: 1.75 }}>
                  Fill out the form below and a SkipTheDealerships.ai account specialist will
                  contact you within 1 business day.
                </p>
              </motion.div>

              {formSent ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  style={{
                    textAlign: 'center',
                    background: 'rgba(26,158,133,0.08)',
                    border: `1px solid ${c.tealBorder}`,
                    borderRadius: 16,
                    padding: '48px 32px',
                  }}
                >
                  <div style={{ fontSize: 40, marginBottom: 16 }}>✅</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color: c.navy, marginBottom: 8 }}>
                    Application sent!
                  </div>
                  <div style={{ color: c.textMid, fontSize: 15 }}>
                    We'll be in touch within 1 business day. Check your email for confirmation.
                  </div>
                </motion.div>
              ) : (
                <motion.form
                  variants={fadeUp}
                  onSubmit={handleSubmit}
                  style={{
                    background: c.bg,
                    border: `1px solid ${c.border}`,
                    borderRadius: 20,
                    padding: '40px 36px',
                  }}
                >
                  {[
                    { label: 'Your name', key: 'name', type: 'text', placeholder: 'Jane Smith' },
                    {
                      label: 'Dealership name',
                      key: 'dealership',
                      type: 'text',
                      placeholder: 'Sunrise Motors Inc.',
                    },
                    {
                      label: 'Business email',
                      key: 'email',
                      type: 'email',
                      placeholder: 'jane@sunrisemotors.ca',
                    },
                    {
                      label: 'Phone number',
                      key: 'phone',
                      type: 'tel',
                      placeholder: '+1 (416) 555-0100',
                    },
                    {
                      label: 'Current inventory size',
                      key: 'inventory',
                      type: 'text',
                      placeholder: 'e.g. 45 vehicles',
                    },
                  ].map(({ label, key, type, placeholder }) => (
                    <div key={key} style={{ marginBottom: 20 }}>
                      <label
                        style={{
                          display: 'block',
                          fontSize: 13,
                          fontWeight: 600,
                          color: c.navy,
                          marginBottom: 6,
                        }}
                      >
                        {label}
                      </label>
                      <input
                        type={type}
                        placeholder={placeholder}
                        value={form[key]}
                        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                        required
                        style={{
                          width: '100%',
                          padding: '12px 14px',
                          fontSize: 15,
                          border: `1px solid ${c.border}`,
                          borderRadius: 10,
                          outline: 'none',
                          background: c.white,
                          color: c.navy,
                          boxSizing: 'border-box',
                        }}
                      />
                    </div>
                  ))}

                  <div style={{ marginBottom: 28 }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: 13,
                        fontWeight: 600,
                        color: c.navy,
                        marginBottom: 6,
                      }}
                    >
                      Plan of interest
                    </label>
                    <select
                      value={form.tier}
                      onChange={e => setForm(f => ({ ...f, tier: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '12px 14px',
                        fontSize: 15,
                        border: `1px solid ${c.border}`,
                        borderRadius: 10,
                        outline: 'none',
                        background: c.white,
                        color: c.navy,
                        boxSizing: 'border-box',
                      }}
                    >
                      <option>Dealer Starter — $0.00/mo (Early Bird, first 100 dealers)</option>
                      <option>Dealer Pro — $999.99/mo</option>
                      <option>Enterprise Fleet — Custom pricing</option>
                    </select>
                  </div>

                  {submitError && (
                    <div
                      style={{
                        background: 'rgba(229,62,62,0.08)',
                        border: '1px solid rgba(229,62,62,0.3)',
                        borderRadius: 10,
                        padding: '12px 16px',
                        marginBottom: 16,
                        fontSize: 14,
                        color: '#c53030',
                      }}
                    >
                      {submitError}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    style={{
                      width: '100%',
                      background: submitting ? '#7A8C99' : c.teal,
                      color: '#fff',
                      fontWeight: 700,
                      fontSize: 16,
                      padding: '16px',
                      borderRadius: 12,
                      border: 'none',
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      transition: 'background 0.2s',
                    }}
                  >
                    {submitting ? 'Submitting…' : 'Submit Application →'}
                  </button>
                  <p
                    style={{ textAlign: 'center', fontSize: 12, color: c.textLight, marginTop: 16 }}
                  >
                    No credit card required · Response within 1 business day · Cancel anytime
                  </p>
                </motion.form>
              )}
            </AnimatedSection>
          </div>
        </section>

        {/* ── FOOTER CTA ── */}
        <section
          style={{
            padding: '60px 32px',
            background: c.bgAlt,
            borderTop: `1px solid ${c.border}`,
            textAlign: 'center',
          }}
        >
          <p style={{ fontSize: 13, color: c.textLight }}>
            Questions? Email{' '}
            <a href="mailto:sales@skipthedealerships.ca" style={{ color: c.teal, fontWeight: 600 }}>
              sales@skipthedealerships.ca
            </a>{' '}
            · Canadian dealers only · GST/HST registered business required
          </p>
        </section>
      </div>
    </>
  );
}
